import { FC } from 'react';

import classNames from 'classnames';
import { PrismicImageWithAlt } from '@/components';

import {
  ImageWithTextSideBySideSlice,
  ImageWithTextSideBySideSliceWithImageGallery,
} from 'prismicio-types';
import { useFilteredSliceItemsByLocationSlug } from '@/hooks';
import { getContentAlign, getContentVerticalAlign } from '@/utils';
import { resolveImageRadiusToken } from '../../../theme/tokens';

import style from './withimagegalleryvariation.module.scss';

export const WithImageGalleryVariation: FC<{
  slice: ImageWithTextSideBySideSliceWithImageGallery &
    ImageWithTextSideBySideSlice;
}> = ({ slice }) => {
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(slice?.items);
  const {
    content_horizontal_align,
    content_full_width,
    content_vertical_align,
    image_corner_preset,
    item_description_color,
    top_image,
    bottom_left_image,
    bottom_right_image,
    item_title_color,
    layout_direction,
  } = slice.primary;

  const alignText = content_horizontal_align ?? 'center';
  const layoutDirection = layout_direction ?? 'left';
  const horizontalAlign = getContentAlign(
    content_horizontal_align as 'start' | 'left' | 'center' | 'end' | 'right',
  );

  const bodyVariables = {
    '--content-padding-left': layoutDirection === 'left' ? '13.6%' : '24px',
    '--content-padding-right': layoutDirection === 'left' ? '24px' : '13.6%',
  };

  return (
    <>
      <div
        className={classNames(style.body, {
          [style.body__left]: layoutDirection === 'left',
        })}
        style={{
          alignItems: horizontalAlign,
          textAlign: alignText,
          alignSelf: getContentVerticalAlign(content_vertical_align),
          ...bodyVariables,
        }}
      >
        {filteredItems &&
          filteredItems?.map((option, index) => (
            <div className={style.content__option_item} key={index}>
              {option.title && (
                <p
                  className={style.title}
                  style={{ color: item_title_color as string }}
                >
                  {option.title}
                </p>
              )}
              {option.description && (
                <p
                  className={style.description}
                  style={{ color: item_description_color as string }}
                >
                  {option.description}
                </p>
              )}
            </div>
          ))}
      </div>
      <div
        className={classNames(style.figure_container, {
          [style.figure_container__left]: layoutDirection === 'left',
        })}
      >
        <figure
          className={classNames(
            style.figure,
            style.top_image,
            {
              [style.figure_full_width_left]:
                !!content_full_width && layoutDirection === 'left',
              [style.figure_full_width_right]:
                !!content_full_width && layoutDirection === 'right',
            },
            resolveImageRadiusToken(image_corner_preset as string),
          )}
        >
          <PrismicImageWithAlt
            field={top_image}
            fill
            showPlaceholderIfEmpty
            style={{ objectFit: 'cover' }}
          />
        </figure>
        <figure
          className={classNames(
            style.figure,
            style.bottom_image,
            {
              [style.figure_full_width_left]:
                !!content_full_width && layoutDirection === 'left',
            },
            resolveImageRadiusToken(image_corner_preset as string),
          )}
        >
          <PrismicImageWithAlt
            field={bottom_left_image}
            fill
            showPlaceholderIfEmpty
            style={{ objectFit: 'cover' }}
          />
        </figure>
        <figure
          className={classNames(
            style.figure,
            style.bottom_image,
            {
              [style.figure_full_width_right]:
                !!content_full_width && layoutDirection === 'right',
            },
            resolveImageRadiusToken(image_corner_preset as string),
          )}
        >
          <PrismicImageWithAlt
            field={bottom_right_image}
            fill
            showPlaceholderIfEmpty
            style={{ objectFit: 'cover' }}
          />
        </figure>
      </div>
    </>
  );
};
